import { useState, useEffect } from 'react';
import { setAgentPrivateKey } from '../utils/privateKey';
import { useInstanceStore } from '../stores/UseInstanceStore';

/**
 * Modal for entering or exporting a Hyperliquid agent private key.
 *
 * Import mode (default): user pastes a key to store in localStorage.
 * Export mode (exportKey prop provided): shows the key read-only with a copy button,
 * useful for transferring the key to another browser (e.g. MetaMask mobile).
 */
export default function AgentKeyModal({
  isOpen,
  portfolioId,
  isTestnet = false,
  portfolioName = '',
  exportKey = null,   // when set, renders in export/copy mode instead of import mode
  onSave,
  onClose,
}) {
  const [privateKey, setPrivateKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [copied, setCopied] = useState(false);

  const isExportMode = !!exportKey;
  const bumpAgentKeyVersion = useInstanceStore((state) => state.bumpAgentKeyVersion);

  // Reset fields each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setPrivateKey('');
      setShowKey(false);
      setValidationError('');
      setCopied(false);
    }
  }, [isOpen]);

  // ESC key dismissal + body scroll lock
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSave = () => {
    const key = privateKey.trim();
    if (!key) {
      setValidationError('Please enter your agent private key.');
      return;
    }
    if (!key.startsWith('0x') || key.length !== 66) {
      setValidationError(
        'Invalid format. Expected a 0x-prefixed 32-byte hex string (66 characters total).'
      );
      return;
    }
    setAgentPrivateKey(portfolioId, key, isTestnet);
    bumpAgentKeyVersion();
    onSave(key);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md bg-slate-800 border border-slate-600 rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              {isExportMode ? 'Export Agent Key' : 'Agent Key Required'}
            </h3>
            {portfolioName && (
              <p className="text-sm text-slate-400 mt-0.5 ml-7">{portfolioName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1 rounded hover:bg-slate-700"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isExportMode ? (
          <>
            {/* Export Body */}
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-300">
                Copy this key and paste it into the <strong>Agent Key Required</strong> dialog
                in your other browser (e.g. MetaMask mobile) to enable trading there.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Agent Private Key
                </label>
                <div className="relative">
                  <input
                    readOnly
                    type={showKey ? 'text' : 'password'}
                    value={exportKey}
                    className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-yellow-300 text-sm font-mono pr-20 select-all focus:outline-none"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="text-slate-400 hover:text-white transition-colors p-1"
                      title={showKey ? 'Hide key' : 'Show key'}
                    >
                      {showKey ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(exportKey);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="text-slate-400 hover:text-cyan-400 transition-colors p-1"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {copied && <p className="text-emerald-400 text-xs mt-1.5">Copied to clipboard!</p>}
              </div>
              <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/40 rounded p-3">
                <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-amber-300/90">
                  Keep this key private. Anyone with this key can trade on behalf of this portfolio.
                </p>
              </div>
            </div>
            {/* Export Footer */}
            <div className="flex justify-end p-5 border-t border-slate-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Import Body */}
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-300">
                This Hyperliquid portfolio requires an agent private key to access trading data.
                Paste the key that was provided when the portfolio was created (e.g. via OpenClaw or the API).
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Agent Private Key
                </label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={privateKey}
                    onChange={(e) => {
                      setPrivateKey(e.target.value);
                      setValidationError('');
                    }}
                    placeholder="0x..."
                    className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm font-mono pr-10 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    tabIndex={-1}
                    title={showKey ? 'Hide key' : 'Show key'}
                  >
                    {showKey ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationError && (
                  <p className="text-red-400 text-xs mt-1.5">{validationError}</p>
                )}
              </div>
              <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/40 rounded p-3">
                <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-amber-300/90">
                  This key is stored only in your browser&apos;s local storage and is never sent to any server.
                </p>
              </div>
            </div>
            {/* Import Footer */}
            <div className="flex justify-end gap-3 p-5 border-t border-slate-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm text-white bg-cyan-700 hover:bg-cyan-600 rounded transition-colors font-medium"
              >
                Save &amp; Retry
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
